import { HttpContextToken } from '@angular/common/http';

export interface BaseApiResponse<T> {
  isSuccess: boolean;
  statusCode: number;
  message?: string;
  errorCode?: string;
  validationErrors?: Record<string, string>;
  /**
   * The client DB server's UTC offset in minutes (local - UTC) that naive timestamps in this
   * response are expressed in. Captured by the response-unwrap interceptor and fed to
   * TimezoneService, which uses it to convert those values to the viewer's local time.
   */
  clientUtcOffsetMinutes?: number;
  data?: T;
}

/**
 * Opt-in channel for the envelope's `message`, which the response-unwrap interceptor discards
 * when it unwraps `data`. Some endpoints return a translation key there (e.g.
 * "AccountNumberAdded") with `data: true`. A caller sets this context token to a fresh object
 * and the interceptor writes the envelope's message into it.
 */
export interface ApiMessageSink {
  message?: string;
}
export const API_MESSAGE_SINK = new HttpContextToken<ApiMessageSink | null>(() => null);

export type BaseApiResponseNoData = BaseApiResponse<null>;

export interface PaginatedApiData<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export class ApiResponseError extends Error {
  readonly statusCode: number;
  readonly errorCode?: string;
  readonly validationErrors?: Record<string, string>;

  constructor(response: Partial<BaseApiResponse<unknown>>) {
    super(response.message || 'Request failed');
    this.name = 'ApiResponseError';
    this.statusCode = response.statusCode ?? 500;
    this.errorCode = response.errorCode;
    this.validationErrors = response.validationErrors;
  }
}

interface UnwrapOptions {
  allowUndefinedData?: boolean;
}

/**
 * Ensures API responses from the backoffice API are consistently unwrapped.
 * Throws ApiResponseError when the backend marks the request as unsuccessful.
 */
export function unwrapApiResponse<T>(
  response: BaseApiResponse<T>,
  options?: UnwrapOptions
): T {
  if (!response) {
    throw new ApiResponseError({
      message: 'Empty API response',
      statusCode: 500,
    });
  }

  if (!response.isSuccess) {
    throw new ApiResponseError(response);
  }

  if (response.data === undefined && !options?.allowUndefinedData) {
    throw new ApiResponseError({
      ...response,
      message: response.message || 'API response is missing data',
    });
  }

  return response.data as T;
}

